import { SortOptions, type CircleSearchParam, type SortOptionsType } from "~/types/entity/cWebCatalog";

const SORT_OPTION_VALUES: ReadonlySet<number> = new Set<number>(Object.values(SortOptions));

/**
 * URL 等の外部入力を SortOptionsType へ正規化する。
 * SortOptions に含まれない値 (空文字由来の 0 以外の NaN、範囲外の数値など) は「未指定 (undefined)」として扱い、
 * 意図しないソート順 (例: `?sort=` → Number("") = 0 = Date) が state に入らないようにする。
 * ADR-020: 未指定は state 層で保持し、実効ソートは resolveCircleSort で解決する。
 */
export const toSortOption = (value: number | string | null | undefined): SortOptionsType | undefined => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }
  const n = typeof value === "number" ? value : Number(value);
  return SORT_OPTION_VALUES.has(n) ? (n as SortOptionsType) : undefined;
};

/**
 * UI 上のソート未指定を、API / 一覧表示用の実効ソートへ解決する。
 * - 明示指定あり → そのまま
 * - 未指定 + keyword（空白のみ除く）→ RankingScore（閾値カスケードを避け、スクロールで低スコアも見える）
 * - 未指定 + keyword なし → Space（サークル一覧 画面の既定のソート順。 2026年7月15日以前 ヘッダー部分のキーワード検索が 新着順 だったがそれは仕様変更となった）
 */
export const resolveCircleSort = (param: Pick<CircleSearchParam, "sort" | "keyword">): SortOptionsType => {
  if (param.sort !== undefined) {
    return param.sort;
  }
  return param.keyword?.trim() ? SortOptions.RankingScore : SortOptions.Space;
};

/**
 * rankingScoreThreshold を API 向けに正規化する。
 * - 非有限は undefined
 * - 0 以下は undefined（閾値なし。percent 0 / 100 もここに入る）
 * - それ以外は 0–1 に clamp（上限 1）
 */
export const clampRankingScoreThreshold = (value: number | undefined | null): number | undefined => {
  if (value == null || !Number.isFinite(value)) {
    return undefined;
  }
  if (value <= 0) {
    return undefined;
  }
  return Math.min(1, value);
};
